/** @typedef {{ title: string, text: string }} ProductGuideStep */
/** @typedef {{ id: string, title: string, lead: string, steps: ProductGuideStep[], tips?: string[], ctaLabel?: string }} ProductGuide */

/** @type {Record<string, ProductGuide>} */
export const PRODUCT_GUIDES = {
  'setup-intro': {
    id: 'setup-intro',
    title: 'Добро пожаловать в AI-Tenders',
    lead: 'Сначала настроим профиль: без критериев победы и поискового ключа анализ закупок недоступен. Это займёт несколько минут.',
    steps: [
      {
        title: 'Критерии победы',
        text: 'Опишите, при каких условиях вы обычно участвуете в закупках: отрасль, тип работ, суммы, регионы и ограничения. Эти данные нужны для оценки шансов на победу и разбора документов по каждой закупке.',
      },
      {
        title: 'Поисковый ключ',
        text: 'Задайте ключевые слова и фильтры — регион, тип закупки, диапазон цен. От этого зависит, какие закупки будут попадать в выдачу поиска.',
      },
      {
        title: 'Что дальше',
        text: 'По желанию подключите личный аккаунт Tenderplan и импортируйте свои поисковые ключи. Затем короткий опрос поможет заполнить критерии и ключ; параметры поиска вы настраиваете сами.',
      },
      {
        title: 'Обратная связь',
        text: 'Если что-то непонятно или хотите предложить улучшение — нажмите «Связь» в меню. Можно отправить предложение, отзыв или сообщение об ошибке и приложить скриншот.',
      },
    ],
    ctaLabel: 'Далее к настройке',
  },
  search: {
    id: 'search',
    title: 'Поиск закупок',
    lead: 'Здесь вы подбираете подходящие закупки, просматриваете условия и отправляете отобранные на анализ в рабочий список.',
    steps: [
      { title: 'Поисковый ключ', text: 'Выберите существующий ключ или создайте новый: укажите ключевые слова, регион, тип закупки и диапазон цен. Ключ можно изменить в любой момент.' },
      { title: 'Фильтр и поиск', text: 'Уточните условия кнопкой «Фильтр», затем нажмите «Поиск». Результаты обновятся в таблице ниже.' },
      { title: 'Активный список', text: 'Выберите список, куда попадут закупки после анализа. Так вы сразу собираете рабочую подборку, с которой будете работать дальше.' },
      { title: 'Отправка на анализ', text: 'Отметьте нужные строки и нажмите «Отправить на анализ». У закупки должны быть приложенные документы — без них разбор невозможен.' },
    ],
    tips: ['Перед первым запуском анализа заполните критерии победы в личном кабинете — раздел «Справочники».'],
  },
  lists: {
    id: 'lists',
    title: 'Списки и канбан',
    lead: 'Рабочее место по уже отобранным закупкам: здесь видно, на каком этапе каждая закупка и готов ли по ней анализ.',
    steps: [
      { title: 'Вкладки списков', text: 'Переключайтесь между списками во вкладках — можно держать открытыми несколько списков одновременно.' },
      { title: 'Таблица и канбан', text: 'В таблице — все закупки списка с основными полями и статусом анализа. В канбане — те же закупки, разложенные по этапам работы: рассмотрение, подача, ожидание результата.' },
      { title: 'Этапы', text: 'Настройте названия и порядок этапов кнопкой «Этапы». Общий шаблон для новых списков задаётся в личном кабинете — раздел «Настройки».' },
      { title: 'Карточка закупки', text: 'Нажмите на строку, чтобы открыть подробности: данные закупки, результаты анализа, риски и дорожную карту подготовки к подаче.' },
    ],
  },
  'cabinet-profile': {
    id: 'cabinet-profile',
    title: 'Профиль',
    lead: 'Контактные данные и настройки уведомлений на электронную почту.',
    steps: [
      { title: 'Контактные данные', text: 'Укажите имя, организацию и телефон — они используются для связи с вами и в сервисных письмах от сервиса.' },
      { title: 'Почта и уведомления', text: 'На указанный адрес приходят подтверждения регистрации, напоминания об оплате и, если включено, письма о просроченных задачах дорожной карты. Настройки типов писем — в разделе «Настройки».' },
    ],
  },
  'cabinet-settings': {
    id: 'cabinet-settings',
    title: 'Настройки',
    lead: 'Шаблон этапов канбана, состав анализа закупки и почтовые уведомления.',
    steps: [
      { title: 'Этапы канбана', text: 'Задайте шаблон этапов для новых списков: названия, порядок, при необходимости — удаление лишних. Для уже созданного списка этапы настраиваются отдельно — в разделе «Списки» или на странице списка.' },
      { title: 'Состав анализа', text: 'Выберите, какие блоки включать при автоматическом анализе: критерии конкурса, список товаров, риски, дорожная карта и другие. Чем больше блоков включено, тем больше токенов расходуется на одну закупку.' },
      { title: 'Уведомления', text: 'Отметьте, о чём присылать письма: изменения по оплате, низкий баланс токенов, просроченные задачи по подготовке заявки.' },
    ],
  },
  'cabinet-integration': {
    id: 'cabinet-integration',
    title: 'Интеграция Tenderplan',
    lead: 'Подключение вашего аккаунта Tenderplan для работы с персональными ключами поиска.',
    steps: [
      { title: 'Подключение', text: 'Вставьте API-ключ из личного кабинета Tenderplan — ваши поисковые ключи подтянутся в сервис автоматически. После подключения их можно выбирать на странице поиска.' },
      { title: 'Без интеграции', text: 'Поиск закупок доступен и без подключения Tenderplan. Интеграция нужна, если вы уже ведёте ключи в Tenderplan и хотите использовать их здесь.' },
    ],
  },
  'cabinet-catalog': {
    id: 'cabinet-catalog',
    title: 'Справочники',
    lead: 'Справочные данные для анализа закупок: ваши критерии участия и прайс по номенклатуре.',
    steps: [
      { title: 'Критерии победы', text: 'Опишите условия, при которых вы участвуете в закупках. Добавьте критерии вручную или воспользуйтесь опросом «Подобрать по тесту» — он подскажет формулировки под ваш профиль.' },
      { title: 'Зачем нужны критерии', text: 'Без хотя бы одного заполненного критерия с названием анализ закупок не запускается. Критерии также учитываются при оценке вероятности победы.' },
      { title: 'Общая таблица товаров и услуг', text: 'Кнопка «Список товаров и услуг» открывает ваш прайс: загрузите файл Excel или добавьте строки с наименованием, типом и ценой. Таблицу можно редактировать и скачивать обратно.' },
      { title: 'Зачем нужен прайс', text: 'При разборе позиций закупки сервис сопоставляет их с вашей таблицей. Так проще понять, есть ли у вас нужная номенклатура и укладывается ли предложение в условия закупки.' },
    ],
  },
  'cabinet-archives': {
    id: 'cabinet-archives',
    title: 'Списки',
    lead: 'Папки и списки для группировки закупок по проектам, направлениям или этапам работы.',
    steps: [
      { title: 'Структура', text: 'Создавайте папки и списки, перетаскивайте элементы в дереве. Так удобно разделять закупки по клиентам, регионам или типам работ.' },
      { title: 'Этапы списка', text: 'У каждого списка могут быть свои этапы канбана — не только общий шаблон из настроек. Этапы настраиваются на странице списка кнопкой «Этапы».' },
    ],
  },
  'cabinet-billing': {
    id: 'cabinet-billing',
    title: 'Биллинг',
    lead: 'Баланс токенов, тарифный план, докупка и история платежей.',
    steps: [
      { title: 'Баланс', text: 'Токены расходуются при запуске анализа закупки, вопросах к ИИ и других операциях с искусственным интеллектом. Следите за остатком перед отправкой большой партии закупок на анализ.' },
      { title: 'Тариф и докупка', text: 'Выберите подходящий тарифный план или докупите нужное количество токенов. Оплата банковской картой — прямо в этом разделе.' },
      { title: 'Журнал списаний', text: 'В истории видно, когда и на какие операции ушли токены — удобно для контроля расходов за период.' },
    ],
  },
  'cabinet-history': {
    id: 'cabinet-history',
    title: 'История',
    lead: 'Хронология ваших действий в сервисе.',
    steps: [
      { title: 'События', text: 'Просматривайте, что вы делали с закупками и списками: добавление в списки, запуск анализа, смена этапов. Удобно, если нужно вспомнить, когда была начата работа по закупке.' },
    ],
  },
  'tender-overview': {
    id: 'tender-overview',
    title: 'Основная информация',
    lead: 'Сводка по закупке: реквизиты, документы, ход анализа и статус в вашем списке.',
    steps: [
      { title: 'Данные закупки', text: 'Номер, заказчик, сроки подачи и приложенные документы — всё, что нужно для первичной оценки, можно посмотреть здесь.' },
      { title: 'Ход анализа', text: 'Кольцо прогресса показывает, какие этапы анализа уже выполнены. Кнопка «Обновить анализ» запускает пересчёт по документам закупки.' },
      { title: 'Статус в списке', text: 'Если закупка добавлена в ваш список, здесь можно сменить этап на канбане — например, перевести с «На рассмотрении» на «Подаёмся».' },
    ],
  },
  'tender-criteria': {
    id: 'tender-criteria',
    title: 'Критерии конкурса',
    lead: 'Требования и условия оценки заявок, извлечённые из документов закупки.',
    steps: [
      { title: 'Структура', text: 'Критерии и подкритерии собраны из извещения и приложений и выстроены в иерархию — так проще ориентироваться в объёмных документах.' },
      { title: 'Оценка заявок', text: 'У каждого пункта указаны название, пояснение и доля в итоговой оценке. При необходимости добавьте или измените строки вручную.' },
      { title: 'Обновление', text: 'Кнопка «Обновить» пересчитает разбор критериев по актуальным документам закупки.' },
    ],
  },
  'tender-products': {
    id: 'tender-products',
    title: 'Список товаров',
    lead: 'Позиции закупки: номенклатура, объёмы и цены из документов — основа для расчёта вашего предложения.',
    steps: [
      { title: 'Позиции', text: 'Здесь перечислены товары и услуги из документов закупки: наименование, количество, единица измерения и цена из извещения.' },
      { title: 'Сверка с прайсом', text: 'Если в личном кабинете в разделе «Справочники» загружена общая таблица товаров и услуг, по этим позициям можно сопоставить ваши цены и оценить, насколько выгодно участвовать в закупке.' },
    ],
  },
  'tender-win-probability': {
    id: 'tender-win-probability',
    title: 'Вероятность победы',
    lead: 'Оценка шансов на победу с учётом ваших критериев участия и опыта прошлых закупок.',
    steps: [
      { title: 'Оценка', text: 'Процент и текстовое пояснение показывают, насколько закупка соответствует вашему профилю участника.' },
      { title: 'На что обратить внимание', text: 'В пояснении выделены сильные и слабые стороны вашей заявки по этой закупке — они помогут принять решение: участвовать, доработать предложение или отказаться.' },
    ],
  },
  'tender-compliance': {
    id: 'tender-compliance',
    title: 'Риски закупки',
    lead: 'Проверка документов закупки на типовые риски и спорные формулировки.',
    steps: [
      { title: 'Отчёт', text: 'После анализа документов здесь появляется список замечаний с пояснениями — что может создать проблемы при подаче или исполнении контракта.' },
      { title: 'Работа с рисками', text: 'Отмечайте пункты как принятые или отклонённые — так удобнее вести внутреннюю проверку и фиксировать, с чем команда согласна участвовать, а с чем нет.' },
    ],
  },
  'tender-roadmap': {
    id: 'tender-roadmap',
    title: 'Дорожная карта',
    lead: 'Чек-лист подготовки к подаче заявки: задачи, сроки и ключевые шаги до даты окончания приёма заявок.',
    steps: [
      { title: 'Задачи', text: 'Список дел с ориентировочными сроками — от сбора документов до финальной проверки заявки. Задачи можно отмечать выполненными или пропускать.' },
      { title: 'Просрочка', text: 'Просроченные задачи выделяются здесь, а также в таблице списков и на канбане — чтобы не пропустить важный срок.' },
    ],
  },
  'tender-ask-ai': {
    id: 'tender-ask-ai',
    title: 'Спросить у ИИ',
    lead: 'Задавайте вопросы по документам этой закупки — ответы формируются на основе загруженных файлов.',
    steps: [
      { title: 'Как пользоваться', text: 'Сформулируйте вопрос по техническому заданию, срокам, требованиям к участнику или любому фрагменту документов. Ответ опирается только на материалы этой закупки.' },
      { title: 'Расход токенов', text: 'За каждый вопрос с баланса списываются токены — так же, как за отдельную операцию анализа. Учитывайте это при большом числе уточнений.' },
    ],
  },
}

/**
 * @param {string} guideId
 * @returns {ProductGuide | null}
 */
export function getProductGuide(guideId) {
  if (!guideId) return null
  return PRODUCT_GUIDES[guideId] ?? null
}

/** @param {string} tabKey */
export function cabinetGuideId(tabKey) {
  return tabKey ? `cabinet-${tabKey}` : ''
}

const TENDER_GUIDE_MAP = {
  overview: 'tender-overview',
  criteria: 'tender-criteria',
  products: 'tender-products',
  'win-probability': 'tender-win-probability',
  compliance: 'tender-compliance',
  roadmap: 'tender-roadmap',
  'ask-ai': 'tender-ask-ai',
}

/** @param {string} tabKey */
export function tenderGuideId(tabKey) {
  return TENDER_GUIDE_MAP[tabKey] ?? ''
}
